import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BackToTopButton from "@/components/common/BackToTopButton";

// Background photo component
const BackgroundPhoto = ({
  top,
  left,
  width,
  height,
  delay,
  src,
}: {
  top: number;
  left: number;
  width: string;
  height: string;
  delay: number;
  src: string;
}) => (
  <div
    className="absolute rounded-xl overflow-hidden"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: width,
      height: height,
      animation: `float 20s ease-in-out ${delay}s infinite`,
      zIndex: 1,
    }}
  >
    <img
      src={src}
      alt=""
      loading="eager"
      className="w-full h-full object-cover opacity-70 blur-xs"
      style={{ transform: "scale(1.1)" }}
    />
  </div>
);

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  const [displayPhotos, setDisplayPhotos] = useState<Array<any>>([]);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Background photos
  const photos = [
    {
      width: "450px",
      height: "350px",
      top: 15,
      left: 10,
      delay: 0,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG1.jpg",
    },
    {
      width: "400px",
      height: "300px",
      top: 15,
      left: 60,
      delay: 1,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG2.jpg",
    },
    {
      width: "420px",
      height: "320px",
      top: 60,
      left: 10,
      delay: 2,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG3.jpg",
    },
    {
      width: "430px",
      height: "330px",
      top: 60,
      left: 60,
      delay: 2.5,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG5.jpg",
    },
  ];

  // Listen for window resize and update state
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    updatePhotos();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update photos when window width changes
  useEffect(() => {
    updatePhotos();
  }, [windowWidth]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Responsive photo config
  const updatePhotos = () => {
    if (windowWidth < 640) {
      setDisplayPhotos(photos.slice(0, 1));
    } else if (windowWidth < 1024) {
      setDisplayPhotos(photos.slice(0, 2));
    } else {
      setDisplayPhotos(photos);
    }
  };

  return (
    <div className="relative w-full min-h-screen flex flex-col bg-black text-white overflow-hidden no-scrollbar">
      {/* Animation style */}
      <style>
        {`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-10px, 10px) rotate(1deg); }
          50% { transform: translate(0, 20px) rotate(0deg); }
          75% { transform: translate(10px, 10px) rotate(-1deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        `}
      </style>

      {/* Background Photos Layer */}
      <div className="absolute inset-0 overflow-hidden">
        {displayPhotos.map((photo, index) => (
          <BackgroundPhoto
            key={index}
            width={windowWidth < 640 ? "100%" : photo.width}
            height={windowWidth < 640 ? "100%" : photo.height}
            top={windowWidth < 640 ? 0 : photo.top}
            left={windowWidth < 640 ? 0 : photo.left}
            delay={photo.delay}
            src={photo.src}
          />
        ))}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[2]"></div>
      </div>

      {/* Header */}
      <Header showHomeButton={true} />

      {/* Main Content */}
      <main className="flex-grow flex items-start justify-center px-4 py-6 sm:py-8 overflow-y-auto no-scrollbar">
        <div className="w-full max-w-3xl p-6 sm:p-8 rounded-xl border border-white/20 bg-black/40 backdrop-blur-md shadow-2xl z-10 my-8 no-scrollbar">
          <h1 className="text-2xl sm:text-3xl font-light mb-6 text-white/90">
            Terms of Service
          </h1>

          <div className="space-y-6 text-sm sm:text-base text-gray-300 leading-relaxed">
            <p className="font-semibold text-white">Waytora Pty Ltd</p>
            <p>
              Last updated: <span className="font-semibold">1 May 2025</span>
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              1. Introduction
            </h2>
            <p>
              These Terms of Service ("<strong>Terms</strong>") form a binding
              agreement between you ("<strong>you</strong>" or "
              <strong>User</strong>") and <strong>Waytora Pty Ltd</strong> (ABN
              ATD) ("<strong>Waytora</strong>", "<strong>we</strong>", "
              <strong>our</strong>" or "<strong>us</strong>") regarding your
              access to and use of <strong>waytora.com</strong>, related
              domains, mobile or desktop applications, APIs, emails, widgets and
              any other online products or services that link to or reference
              these Terms (collectively, the "<strong>Services</strong>").
              <br />
              By accessing, browsing or using any part of the Services you
              acknowledge that you have read, understood and agree to be bound
              by these Terms and by our{" "}
              <a
                href="/"
                className="text-indigo-400 underline hover:text-indigo-300"
              >
                Privacy Policy
              </a>{" "}
              (which is incorporated by reference).
              <br />
              If you do not agree to these Terms, you must not use the Services.
            </p>
            <h3 className="font-semibold mt-4">1.1 Eligibility</h3>
            <p>
              You must be at least <strong>13 years</strong> old and able to
              form a legally binding contract in your jurisdiction to use the
              Services. If you are using the Services on behalf of another
              person or entity, you represent that you have authority to bind
              that person or entity to these Terms.
            </p>
            <h3 className="font-semibold mt-4">1.2 Modifications</h3>
            <p>
              We may update these Terms from time to time. Material changes will
              be notified via email or in app banner <strong>14 days</strong>{" "}
              before they take effect. Your continued use of the Services after
              the effective date constitutes acceptance of the revised Terms.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              2. The Waytora Platform
            </h2>
            <p>
              Waytora provides tools that help Users research, create, organise,
              share and book travel itineraries and related products ("
              <strong>Trips</strong>"). Waytora is <strong>not</strong> itself a
              travel agency, airline, hotel, tour operator or insurer. Bookings
              are fulfilled by independent third party suppliers ("
              <strong>Travel Partners</strong>"). Waytora does not control and
              is not responsible for Travel Partner content, availability,
              pricing or conduct.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              3. Accounts & Security
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>Registration.</strong> To access certain features you
                must create an account using a valid email address or a
                supported single sign on provider (e.g. Google). You agree to
                provide accurate, current and complete information.
              </li>
              <li>
                <strong>Credentials.</strong> You are responsible for
                maintaining the confidentiality of your login credentials and
                for all activities that occur under your account. Notify us
                immediately of any unauthorised use.
              </li>
              <li>
                <strong>Account Closure.</strong> You may close your account at
                any time in the app settings or by emailing{" "}
                <a
                  href="mailto:support@waytora.com"
                  className="text-indigo-400 underline hover:text-indigo-300"
                >
                  support@waytora.com
                </a>
                . We may suspend or terminate your account if you breach these
                Terms or applicable laws.
              </li>
            </ul>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              4. User Content
            </h2>
            <h3 className="font-semibold mt-4">4.1 Definition</h3>
            <p>
              "<strong>User Content</strong>" means text, photos, graphics,
              itineraries, reviews, messages, ratings or other materials that
              you submit, upload, publish or otherwise make available through
              the Services.
            </p>
            <h3 className="font-semibold mt-4">4.2 Your Responsibility</h3>
            <p>
              You retain all ownership rights in your User Content but grant
              Waytora the licence described below. You are solely responsible
              for User Content you provide and warrant that you have all rights
              necessary to grant the licence and that your content, and our use
              of it, will not violate any law or infringe any third party
              rights.
            </p>
            <h3 className="font-semibold mt-4">4.3 Licence</h3>
            <p>
              By posting User Content you grant Waytora a non exclusive,
              worldwide, royalty free, sublicensable, transferable licence to
              host, store, reproduce, modify (e.g. reformat), perform, display,
              distribute and otherwise use the User Content in connection with
              operating, improving and promoting the Services. This licence ends
              when your User Content is deleted from our systems.
            </p>
            <h3 className="font-semibold mt-4">4.4 Removal</h3>
            <p>
              We may remove or disable access to User Content that we believe
              violates these Terms or is otherwise harmful.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              5. Acceptable Use
            </h2>
            <p>
              You agree <strong>not</strong> to:
            </p>
            <ol className="list-decimal pl-6 space-y-2 mt-2">
              <li>
                Post unlawful, fraudulent, defamatory, abusive, hateful,
                harassing, obscene or otherwise objectionable content;
              </li>
              <li>
                Impersonate any person or entity or misrepresent your
                affiliation;
              </li>
              <li>
                Collect, scrape or harvest data from the Services without
                permission;
              </li>
              <li>
                Reverse engineer, decompile or disassemble any portion of the
                Services except as authorised by law;
              </li>
              <li>
                Interfere with or disrupt the operation of the Services or any
                user's enjoyment of them;
              </li>
              <li>
                Use automated scripts to access, copy, or manipulate content;
              </li>
              <li>Upload viruses, malware, or other harmful code;</li>
              <li>Violate any applicable law or third party right.</li>
            </ol>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              6. Bookings & Payments
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>Third Party Contracts.</strong> When you make a booking
                through the Services you enter into a contract directly with the
                relevant Travel Partner. Waytora is <strong>not</strong> a party
                to that contract.
              </li>
              <li>
                <strong>Accuracy & Availability.</strong> We endeavour to
                display accurate prices and availability but cannot guarantee
                that information supplied by Travel Partners is error free. Any
                discrepancies should be resolved with the Travel Partner.
              </li>
              <li>
                <strong>Fees.</strong> Waytora may receive affiliate commissions
                from certain bookings. Where Waytora charges a service fee it
                will be disclosed before checkout.
              </li>
              <li>
                <strong>Payment Processing.</strong> Payments, where applicable,
                are processed by third party payment processors ("Payment
                Processor"). Your payment information is governed by the Payment
                Processor's terms and privacy policy.
              </li>
            </ul>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              7. AI Generated Content
            </h2>
            <p>
              Our AI assistant "Tora" uses language model technology to generate
              suggestions and responses. AI generated output may be incorrect,
              incomplete or inappropriate.{" "}
              <strong>Do not rely solely on AI output</strong> for travel,
              medical, legal or safety decisions. You are responsible for
              verifying information before acting on it.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              8. Intellectual Property
            </h2>
            <p>
              The Services, including all software, design, text, graphics and
              other content, are owned by Waytora or its licensors and are
              protected by copyright, trademark and other laws. Subject to your
              compliance with these Terms, Waytora grants you a limited,
              revocable, non exclusive and non transferable right to access and
              use the Services for personal, non commercial purposes. All rights
              not expressly granted are reserved.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              9. Third Party Links & Services
            </h2>
            <p>
              The Services may contain links to or integrations with third party
              websites, apps, content or services. Waytora does not endorse or
              control these third parties and is{" "}
              <strong>not responsible</strong> for their content or practices.
              Your interactions with third party services are at your own risk
              and subject to their terms.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              10. Termination
            </h2>
            <p>
              We may suspend or terminate your access to all or part of the
              Services, or remove User Content, immediately and without notice
              if: (a) you breach these Terms; (b) we are required to do so by
              law; or (c) we discontinue the Services. Upon termination, the
              licence granted to you will cease and you must stop using the
              Services. Sections 4.3, 5, 7, 8, 11, 12 and 13 survive
              termination.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              11. Disclaimers
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>As Is.</strong> The Services are provided "
                <strong>as is</strong>" and "<strong>as available</strong>"
                without warranties of any kind, express or implied. Waytora
                disclaims all implied warranties including merchantability,
                fitness for a particular purpose, title and non infringement.
              </li>
              <li>
                <strong>No Guarantee.</strong> We do not guarantee that the
                Services or content (including AI output) will be accurate,
                reliable, error free or uninterrupted, or that any defects will
                be corrected.
              </li>
              <li>
                <strong>Travel Risks.</strong> Travel involves risks. You are
                responsible for checking official travel advisories and ensuring
                you comply with entry requirements, visas, vaccinations and
                insurance needs.
              </li>
            </ul>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              12. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, Waytora, its directors,
              employees and affiliates will <strong>not</strong> be liable for
              any indirect, incidental, special, consequential or punitive
              damages, or for any loss of profits, revenues, goodwill, data or
              other intangible losses, arising from or related to your use of
              (or inability to use) the Services, even if advised of the
              possibility of such damages.
              <br />
              Waytora's total liability for any claim arising out of or relating
              to the Services is limited to <strong>AUD ATD100</strong> or the
              amount you paid us in the 12 months preceding the claim, whichever
              is greater. Nothing in these Terms excludes liability that cannot
              be excluded under applicable law (e.g. statutory consumer
              guarantees).
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              13. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold harmless Waytora, its officers,
              directors, employees and agents from and against any claims,
              liabilities, damages, losses and expenses (including reasonable
              legal fees) arising out of or related to: (a) your breach of these
              Terms; (b) your User Content; or (c) your violation of any law or
              third party right.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              14. Governing Law & Dispute Resolution
            </h2>
            <p>
              These Terms are governed by the laws of{" "}
              <strong>New South Wales, Australia</strong>, without regard of
              conflict of laws principles.
              <br />
              Any dispute arising from or relating to these Terms or the
              Services shall be resolved exclusively in the state or federal
              courts located in <strong>Sydney, NSW</strong>. You and Waytora
              consent to venue and personal jurisdiction in those courts.
              However, before litigating, the parties agree to attempt to
              resolve the dispute through good faith negotiations for{" "}
              <strong>30 days</strong>.
            </p>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              15. Miscellaneous
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <strong>Entire Agreement.</strong> These Terms constitute the
                entire agreement between you and Waytora and supersede all prior
                agreements.
              </li>
              <li>
                <strong>Severability.</strong> If any provision is found
                unenforceable, the remaining provisions will remain in full
                effect.
              </li>
              <li>
                <strong>Assignment.</strong> You may not assign or transfer your
                rights or obligations under these Terms without our prior
                written consent. Waytora may assign these Terms in connection
                with a merger, sale or corporate reorganisation.
              </li>
              <li>
                <strong>Waiver.</strong> Waytora's failure to enforce any
                provision is not a waiver of its right to do so later.
              </li>
              <li>
                <strong>Force Majeure.</strong> Waytora is not liable for delays
                or failures caused by events beyond its reasonable control.
              </li>
            </ul>
            <hr className="my-4 border-white/20" />

            <h2 className="text-xl text-white/90 font-medium mt-6">
              16. Contact
            </h2>
            <p>Questions about these Terms?</p>
            <ul className="mt-2">
              <li>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:support@waytora.com"
                  className="text-indigo-400 underline hover:text-indigo-300"
                >
                  support@waytora.com
                </a>
              </li>
              <li>
                <strong>Post:</strong> Waytora Pty Ltd, Level 1, 457 459
                Elizabeth Street, Surry Hills NSW 2010, Australia
              </li>
            </ul>
            <hr className="my-4 border-white/20" />
            <p className="text-xs text-white/60">
              © 2025 Waytora Pty Ltd. All rights reserved.
            </p>
          </div>

          <div className="flex justify-center mt-8">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 text-sm text-white border border-white/30 rounded-lg
              hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              Back
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      <BackToTopButton />
    </div>
  );
};

export default TermsOfServicePage;
